/**
 * Created by DemonRay on 2019/3/22.
 */

import utils from '../../utils'

let defaults = {
  handleColor: '#000000', // the colour of the handle and the line drawn from it
  enabled: true, // whether to start the plugin in the enabled state
  minNodeWidth: 30,
  minNodeHeight: 30,
  triangleSize: 10,
  triangleHitPadding: 10,
  selector: 'node',
  lines: 3,
  padding: 5,

  start: function (sourceNode) {
    // fired when noderesize interaction starts (drag on handle)
  },
  complete: function (sourceNode, targetNodes, addedEntities) {
    // fired when noderesize is done and entities are added
  },
  stop: function (sourceNode) {
    // fired when noderesize interaction is stopped (either complete with added edges or incomplete)
  },
  transformDimensions: function (node, dimensions) {
    return dimensions
  }
}

/**
 * Checks if the point p is inside the triangle p0,p1,p2
 * using barycentric coordinates
 */
function ptInTriangle (p, p0, p1, p2) {
  let A = 1 / 2 * (-p1.y * p2.x + p0.y * (-p1.x + p2.x) + p0.x * (p1.y - p2.y) + p1.x * p2.y)
  let sign = A < 0 ? -1 : 1
  let s = (p0.y * p2.x - p0.x * p2.y + (p2.y - p0.y) * p.x + (p0.x - p2.x) * p.y) * sign
  let t = (p0.x * p1.y - p0.y * p1.x + (p0.y - p1.y) * p.x + (p1.x - p0.x) * p.y) * sign

  return s > 0 && t > 0 && (s + t) < 2 * A * sign
}

function inflateTrianglePoints (p0, p1, p2, padding) {
  if (padding <= 0) {
    return { p0, p1, p2 }
  }

  return {
    p0: { x: p0.x - padding, y: p0.y + padding },
    p1: { x: p1.x + padding, y: p1.y - padding },
    p2: { x: p2.x + padding, y: p2.y + padding }
  }
}

function getHandleTrianglePoints (node, options, zoom) {
  let padding = options.padding * zoom
  let p = node.renderedPosition()
  let width = (node.data('width') || node.width()) * zoom + padding * 2
  let height = (node.data('height') || node.height()) * zoom + padding * 2
  let ts = options.triangleSize * zoom

  let p0 = { x: p.x + width / 2 - ts, y: p.y + height / 2 }
  let p1 = { x: p.x + width / 2, y: p.y + height / 2 - ts }
  let p2 = { x: p.x + width / 2, y: p.y + height / 2 }

  return { p0, p1, p2 }
}

class NodeResize {
  constructor (cy, params) {
    this.cy = cy
    this._container = this.cy.container()
    this._listeners = {}
    this._drawMode = false
    this._drawsClear = true
    this._options = {}
    this._init(params)
  }

  destroy () {
    let data = this._options

    if (!data) {
      return
    }
    data.unbind()
    this._options = {}
  }

  disable () {
    this._options.enabled = false
    this._options.disabled = true
    this.clearDraws()
    this._unbindHandleListener()
  }

  enable () {
    this._options.enabled = true
    this._options.disabled = false
  }

  resize () {
    this.cy.trigger('cyeditor.noderesize-resize')
  }

  drawon () {
    this._drawMode = true
    this._prevUngrabifyState = this.cy.autoungrabify()
    this.cy.autoungrabify(true)
    this.cy.trigger('cyeditor.noderesize-drawon')
  }

  drawoff () {
    this._drawMode = false
    this.cy.autoungrabify(this._prevUngrabifyState)
    this.cy.trigger('cyeditor.noderesize-drawoff')
  }

  _init (params) {
    this._options = utils.extend(true, {}, defaults, params)
    this.canvas = document.createElement('canvas')
    this.ctx = this.canvas.getContext('2d')
    this._container.append(this.canvas)
    this._listeners._sizeCanvas = utils.debounce(this._sizeCanvas, 250).bind(this)
    this._listeners._sizeCanvas()

    this._initEvents()
  }

  _sizeCanvas () {
    let rect = this._container.getBoundingClientRect()
    this.canvas.width = rect.width
    this.canvas.height = rect.height
    utils.css(this.canvas, {
      'position': 'absolute',
      'top': 0,
      'left': 0,
      'zIndex': '999'
    })

    setTimeout(() => {
      let canvasBb = utils.offset(this.canvas)
      let containerBb = utils.offset(this._container)
      utils.css(this.canvas, {
        'top': -(canvasBb.top - containerBb.top) + 'px',
        'left': -(canvasBb.left - containerBb.left) + 'px'
      })
    }, 0)
  }

  clearDraws () {
    if (this._drawsClear) {
      return
    }

    let containerRect = this._container.getBoundingClientRect()

    let w = containerRect.width
    let h = containerRect.height

    this.canvas.getContext('2d').clearRect(0, 0, w, h)
    this._drawsClear = true
  }

  _disableGestures () {
    this._lastPanningEnabled = this.cy.panningEnabled()
    this._lastZoomingEnabled = this.cy.zoomingEnabled()
    this._lastBoxSelectionEnabled = this.cy.boxSelectionEnabled()

    this.cy
      .zoomingEnabled(false)
      .panningEnabled(false)
      .boxSelectionEnabled(false)
  }

  _resetGestures () {
    this.cy
      .zoomingEnabled(this._lastZoomingEnabled)
      .panningEnabled(this._lastPanningEnabled)
      .boxSelectionEnabled(this._lastBoxSelectionEnabled)
  }

  _resetToDefaultState () {
    this.clearDraws()
    this.sourceNode = null
    this._resetGestures()
  }

  _unbindHandleListener () {
    if (!this._listeners.lastMdownHandler) {
      return
    }

    this._container.removeEventListener('mousedown', this._listeners.lastMdownHandler, true)
    this._container.removeEventListener('touchstart', this._listeners.lastMdownHandler, true)
    this._listeners.lastMdownHandler = null
  }

  _getSelectedNode () {
    let selectedNodes = this.cy.$(`${this._options.selector}:selected`)
    if (!selectedNodes || selectedNodes.length === 0) {
      return null
    }

    return selectedNodes[0]
  }

  _redrawPersistentHandle () {
    if (this._drawMode || this._mdownOnHandle || this._grabbingNode) {
      return
    }

    let selectedNode = this._getSelectedNode()
    this.clearDraws()
    this._unbindHandleListener()
    if (!selectedNode) {
      return
    }

    this._activateHandle(selectedNode)
  }

  _activateHandle (node) {
    this._lastActiveId = node.id()
    this._drawHandle(node)
    node.trigger('cyeditor.noderesize-showhandle')
    this._bindHandleListener(node)
  }

  _bindHandleListener (node) {
    let lastPosition = {}

    let mdownHandler = (e) => {
      this._unbindHandleListener()

      let pageX = !e.touches ? e.pageX : e.touches[ 0 ].pageX
      let pageY = !e.touches ? e.pageY : e.touches[ 0 ].pageY
      let x = pageX - utils.offset(this._container).left
      let y = pageY - utils.offset(this._container).top
      lastPosition.x = x
      lastPosition.y = y

      if (e.button !== 0 && !e.touches) {
        return // sorry, no right clicks allowed
      }

      let p = { x: x, y: y }
      let {p0, p1, p2} = getHandleTrianglePoints(node, this._options, this.cy.zoom())
      let hitPadding = this._options.triangleHitPadding * this.cy.zoom()
      let hitTriangle = inflateTrianglePoints(p0, p1, p2, hitPadding)

      if (!ptInTriangle(p, hitTriangle.p0, hitTriangle.p1, hitTriangle.p2)) {
        this._redrawPersistentHandle()
        return
      }

      node.addClass('noderesize-resized')

      this._mdownOnHandle = true

      e.preventDefault()
      e.stopPropagation()

      this.sourceNode = node

      node.trigger('cyeditor.noderesize-start')
      let originalSize = {
        width: node.width(),
        height: node.height()
      }

      let doneMoving = (dmEvent) => {
        if (!this._mdownOnHandle) {
          return
        }

        this._mdownOnHandle = false
        window.removeEventListener('mousemove', moveHandler)
        window.removeEventListener('touchmove', moveHandler)
        this._resetToDefaultState()
        this._redrawPersistentHandle()

        this._options.stop(node)
        node.trigger('cyeditor.noderesize-stop')
        this.cy.trigger('cyeditor.noderesize-resized',
          [
            node,
            originalSize,
            {
              width: node.width(),
              height: node.height()
            }
          ]
        )
      }

      [ 'mouseup', 'touchend', 'touchcancel', 'blur' ].forEach(function (e) {
        utils.once(window, e, doneMoving)
      })
      window.addEventListener('mousemove', moveHandler)
      window.addEventListener('touchmove', moveHandler)
      this._disableGestures()
      this._options.start(node)

      return false
    }

    let moveHandler = (e) => {
      let pageX = !e.touches ? e.pageX : e.touches[ 0 ].pageX
      let pageY = !e.touches ? e.pageY : e.touches[ 0 ].pageY
      let x = pageX - utils.offset(this._container).left
      let y = pageY - utils.offset(this._container).top

      let dx = x - lastPosition.x
      let dy = y - lastPosition.y

      lastPosition.x = x
      lastPosition.y = y
      let keepAspectRatio = e.ctrlKey
      let w = node.data('width') || node.width()
      let h = node.data('height') || node.height()

      if (keepAspectRatio) {
        let aspectRatio = w / h
        if (dy === 0) {
          dy = dx = dx * aspectRatio
        } else {
          dx = dy = (dy < 0 ? Math.min(dx, dy) : Math.max(dx, dy)) * aspectRatio
        }
      }
      dx /= this.cy.zoom()
      dy /= this.cy.zoom()

      let nextDimensions = this._options.transformDimensions(node, {
        width: Math.max(w + dx * 2, this._options.minNodeWidth),
        height: Math.max(h + dy * 2, this._options.minNodeHeight),
        dx,
        dy,
        originalWidth: w,
        originalHeight: h,
        minNodeWidth: this._options.minNodeWidth,
        minNodeHeight: this._options.minNodeHeight,
        keepAspectRatio
      }) || {}

      node.data('width', nextDimensions.width ?? w)
      node.data('height', nextDimensions.height ?? h)

      this.cy.trigger('cyeditor.noderesize-resizing', [ node, {
        width: node.width(),
        height: node.height()
      } ])

      this.clearDraws()
      this._drawHandle(node)

      return false
    }

    this._container.addEventListener('mousedown', mdownHandler, true)
    this._container.addEventListener('touchstart', mdownHandler, true)
    this._listeners.lastMdownHandler = mdownHandler
  }

  _drawHandle (node) {
    this.ctx.fillStyle = this._options.handleColor
    this.ctx.strokeStyle = this._options.handleColor
    let {p0, p1, p2} = getHandleTrianglePoints(node, this._options, this.cy.zoom())

    this.ctx.beginPath()
    this.ctx.moveTo(p2.x, p2.y)
    this.ctx.lineTo(p1.x, p1.y)
    this.ctx.lineTo(p0.x, p0.y)
    this.ctx.closePath()
    this.ctx.fill()

    this._drawsClear = false
  }

  _initEvents () {
    window.addEventListener('resize', this._listeners._sizeCanvas)
    this.cy.on('cyeditor.noderesize-resize', this._listeners._sizeCanvas)
    this._grabbingNode = false

    let hoverTimeout
    this._lastPanningEnabled = this.cy.panningEnabled()
    this._lastZoomingEnabled = this.cy.zoomingEnabled()
    this._lastBoxSelectionEnabled = this.cy.boxSelectionEnabled()

    this.cy.style().selector('.noderesize-resized').css({
      'width': 'data(width)',
      'height': 'data(height)'
    })

    this._listeners.transformHandler = this._redrawPersistentHandle.bind(this)
    this._listeners._startHandler = this._startHandler.bind(this)
    this._listeners.selectionHandler = this._redrawPersistentHandle.bind(this)

    this.cy.bind('zoom pan', this._listeners.transformHandler)

    let hoverHandler = () => {
      if (this._options.disabled || this._drawMode) {
        return // ignore preview nodes
      }

      if (this._mdownOnHandle) { // only handle mdown case
        return false
      }
    }
    let leaveHandler = () => {
      if (this._drawMode) {
        return
      }

      if (this._mdownOnHandle) {
        clearTimeout(hoverTimeout)
      }
    }
    let freeNodeHandler = () => {
      this._grabbingNode = false
    }
    let dragNodeHandler = () => {
      if (this._drawMode) {
        return
      }
      setTimeout(() => this._redrawPersistentHandle(), 50)
    }
    let removeHandler = (e) => {
      let id = e.target.id()

      if (id === this._lastActiveId) {
        setTimeout(() => {
          this._resetToDefaultState()
        }, 16)
      }
    }
    let tapToStartHandler = (e) => {
      let node = e.target

      if (!this.sourceNode) { // must not be active
        setTimeout(() => {
          this.clearDraws() // clear just in case

          this._drawHandle(node)

          node.trigger('cyeditor.noderesize-showhandle')
        }, 16)
      }
    }
    let dragHandler = () => {
      this._grabbingNode = true
    }
    let grabHandler = () => {
      this.clearDraws()
    }
    let selector = this._options.selector
    this.cy.on('mouseover tap', selector, this._listeners._startHandler)
      .on('select unselect', selector, this._listeners.selectionHandler)
      .on('data', selector, this._listeners.selectionHandler)
      .on('mouseover tapdragover', selector, hoverHandler)
      .on('mouseout tapdragout', selector, leaveHandler)
      .on('drag position', selector, dragNodeHandler)
      .on('grab', selector, grabHandler)
      .on('drag', selector, dragHandler)
      .on('free', selector, freeNodeHandler)
      .on('remove', selector, removeHandler)
      .on('tap', selector, tapToStartHandler)

    this._options.unbind = () => {
      window.removeEventListener('resize', this._listeners._sizeCanvas)
      this.cy.off('mouseover', selector, this._listeners._startHandler)
        .off('select', selector, this._listeners.selectionHandler)
        .off('unselect', selector, this._listeners.selectionHandler)
        .off('data', selector, this._listeners.selectionHandler)
        .off('mouseover', selector, hoverHandler)
        .off('mouseout', selector, leaveHandler)
        .off('drag position', selector, dragNodeHandler)
        .off('grab', selector, grabHandler)
        .off('free', selector, freeNodeHandler)
        .off('remove', selector, removeHandler)
        .off('tap', selector, tapToStartHandler)

      this.cy.unbind('zoom pan', this._listeners.transformHandler)
    }
  }

  _startHandler (e) {
    let node = e.target

    if (this._options.disabled || this._drawMode || this._mdownOnHandle || this._grabbingNode || node.isParent()) {
      return // don't override existing handle that's being dragged also don't trigger when grabbing a node etc
    }

    this.clearDraws()
    this._unbindHandleListener()
    this._activateHandle(node)
  }
}

export default (cytoscape) => {
  if (!cytoscape) { return }

  cytoscape('core', 'noderesize', function (options) {
    return new NodeResize(this, options)
  })
}
